import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatJobLogLine } from '@/console/features/jobs/job-logs.utils';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { JobLogsFilter } from '@/shared/filters/entity-filters/job-logs-filter/job-logs-filter';
import { useDirectFilter } from '@/shared/filters/hooks/use-direct-filter/use-direct-filter';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import { useReadJobLogsQuery } from '@/shared/redux/api/jobs/jobs-api';
import { TJobStatus } from '@/shared/types/job-status.types';
import { JobLogFragment } from '@/shared/types/types.generated';

const JOB_LOGS_POLLING_INTERVAL_MS = 20_000;

export const useJobLogs = (
  jobId?: string,
  jobStatus?: TJobStatus,
  shouldConvertTimestamp = false,
) => {
  const { t } = useTranslation();
  const { isJobRunning } = useJobStatus();
  const { formatLocalTime } = useDateUtils();
  const [allLogs, setAllLogs] = useState<JobLogFragment[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const filter = useDirectFilter(new JobLogsFilter(), {
    nextToken: { value: nextToken ?? '', method: 'eq' },
  });

  const {
    data: currentPageData,
    isLoading,
    isError,
    error,
    isFetching,
    pollingEnabled,
  } = usePollingQuery({
    useQueryHook: useReadJobLogsQuery,
    queryArg: { jobId: jobId!, filter },
    queryOptions: { skip: !jobId },
    shouldStopPolling: () => !jobStatus || !isJobRunning(jobStatus),
    pollingInterval: JOB_LOGS_POLLING_INTERVAL_MS,
  });

  useEffect(() => {
    if (!isFetching && currentPageData) {
      setAllLogs((prev) => [
        ...prev,
        ...(currentPageData.readJobLogs?.logs ?? []),
      ]);
      setNextToken(currentPageData.readJobLogs?.nextToken);
    }
  }, [currentPageData, isFetching]);

  const getFormattedLogs = () => {
    if (!allLogs.length && isLoading) {
      return [{ text: t('pipelines:loading_logs') }];
    }

    return allLogs.flatMap((log) =>
      formatJobLogLine(log.message, shouldConvertTimestamp, formatLocalTime),
    );
  };

  const logs = getFormattedLogs();

  return {
    logs: logs.map((log) => log.text).join('\n'),
    logLines: logs,
    isLoading: isLoading || isFetching,
    isError,
    error,
    isFetching: isFetching && pollingEnabled,
  };
};
